import { TypedDocumentNode, gql } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import placeholder from '@sorare/core/src/assets/club/shield_none.png';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Shield } from '@sorare/core/src/atoms/icons/Shield';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyL, HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useGeneratePathWithSearch } from '@sorare/core/src/hooks/useGeneratePathWithSearch';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useFootballEventOnce } from '@sorare/gameplay/src/lib/events';

import { OnboardingPromoRecapDialog } from 'components/onboarding/OnboardingPromoRecapDialog';
import { PlayNowOrLater } from 'components/onboarding/PlayNowOrLater';

import type {
  FootballOnboardingClubSelectionQuery,
  FootballOnboardingClubSelectionQueryVariables,
  FootballOnboardingUpdateFavoriteTeamMutation,
  FootballOnboardingUpdateFavoriteTeamMutationVariables,
} from './__generated__/index.graphql';

const inputPlaceholder = defineMessage({
  id: 'Onboarding.FavoriteClubSelection.inputPlaceholder',
  defaultMessage: 'Search club',
});

const Root = styled.div`
  width: 100%;
  background: linear-gradient(to bottom, #2c2b70 0%, rgba(44, 43, 112, 0) 100%);
  background-size: 100% var(--100vh);
  background-repeat: no-repeat;
  background-attachment: fixed;
  min-height: var(--100vh);
`;
const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  margin: auto;

  @media ${tabletAndAbove} {
    width: 80%;
  }
  @media ${desktopAndAbove} {
    width: 60%;
  }
`;
const ClubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-auto-rows: 1fr;
  gap: var(--double-unit);

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;
const Club = styled(Vertical).attrs({ center: true, gap: 2 })`
  width: 100%;
  height: 100%;
  background: var(--c-nd-50);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--triple-unit);
`;
const EmptyStateWrapper = styled(Vertical).attrs({ center: true, gap: 2 })`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);
`;
const ClubImg = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const FOOTBALL_ONBOARDING_CLUB_SELECTION_QUERY = gql`
  query FootballOnboardingClubSelectionQuery($query: String, $after: String) {
    so5 {
      id
      onboardingAvailableFavoriteTeams(
        sport: FOOTBALL
        query: $query
        after: $after
        first: 100
      ) {
        nodes {
          slug
          name
          pictureUrl
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      onboardingPromotionalEvent(sport: FOOTBALL) {
        id
        myTask {
          id
        }
        so5Leaderboard {
          slug
        }
        ...OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent
      }
    }
  }
  ${OnboardingPromoRecapDialog.fragments.so5OnboardingPromotionalEvent}
` as TypedDocumentNode<
  FootballOnboardingClubSelectionQuery,
  FootballOnboardingClubSelectionQueryVariables
>;

const FOOTBALL_ONBOARDING_UPDATE_FAVORITE_TEAM_MUTATION = gql`
  mutation FootballOnboardingUpdateFavoriteTeamMutation(
    $input: updateFavoriteTeamInput!
  ) {
    updateFavoriteTeam(input: $input) {
      team {
        slug
        upcomingAnyGames(first: 1) {
          id
          date
        }
        ...PlayNowOrLater_teamInterface
      }
      userSportProfile {
        id
        forcedOnboardingSo5Leaderboard {
          slug
        }
        myFavoriteTeamOnboardingSo5Leaderboard {
          slug
        }
        ...PlayNowOrLater_userSportProfileInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${PlayNowOrLater.fragments.teamInterface}
  ${PlayNowOrLater.fragments.userSportProfileInterface}
` as TypedDocumentNode<
  FootballOnboardingUpdateFavoriteTeamMutation,
  FootballOnboardingUpdateFavoriteTeamMutationVariables
>;

export const SelectFavoriteClub = () => {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [mutationPayload, setMutationPayload] = useState<
    FootballOnboardingUpdateFavoriteTeamMutation['updateFavoriteTeam'] | null
  >(null);
  const [query, setQuery] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [dialogPromotionalEvent, setDialogPromotionalEvent] =
    useState<
      FootballOnboardingClubSelectionQuery['so5']['onboardingPromotionalEvent']
    >(null);
  const generatePathWithSearch = useGeneratePathWithSearch();
  const { isMobileApp } = useIsMobileApp();
  const trackOnce = useFootballEventOnce();

  trackOnce('View Onboarding Select Your Club');

  const { data, loading, loadMore } = usePaginatedQuery(
    FOOTBALL_ONBOARDING_CLUB_SELECTION_QUERY,
    {
      connection: 'TeamInterfaceConnection',
      variables: { query },
    }
  );
  const { so5 } = data || {};

  const { nodes: clubs = [], pageInfo } =
    so5?.onboardingAvailableFavoriteTeams || {};
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: pageInfo?.endCursor,
      });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  useDebounce(
    () => {
      setQuery(inputValue);
    },
    500,
    [inputValue]
  );

  const [updateFavoriteClub, { loading: mutationLoading }] = useMutation(
    FOOTBALL_ONBOARDING_UPDATE_FAVORITE_TEAM_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  const { onboardingPromotionalEvent } = so5 || {};

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const onSelectClub = async (favoriteTeamSlug: string) => {
    setSelectedSlug(favoriteTeamSlug);
    const { data: mutationData } = await updateFavoriteClub({
      variables: {
        input: { favoriteTeamSlug, sport: Sport.FOOTBALL },
      },
    });

    const { userSportProfile, team } = mutationData?.updateFavoriteTeam || {};
    const nextGameDate = team?.upcomingAnyGames.at(0)?.date;
    const numberOfDaysUntilNextGame =
      nextGameDate && differenceInDays(nextGameDate, new Date());
    const canGoDirectlyToFavoriteTeamLeaderboard =
      typeof numberOfDaysUntilNextGame === 'number' &&
      numberOfDaysUntilNextGame < 7;
    const {
      forcedOnboardingSo5Leaderboard,
      myFavoriteTeamOnboardingSo5Leaderboard,
    } = userSportProfile || {};

    if (onboardingPromotionalEvent?.myTask && !isMobileApp) {
      setDialogPromotionalEvent(onboardingPromotionalEvent);
    } else if (forcedOnboardingSo5Leaderboard) {
      navigate(
        generatePathWithSearch(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG, {
          slug: forcedOnboardingSo5Leaderboard.slug,
        })
      );
    } else if (
      myFavoriteTeamOnboardingSo5Leaderboard &&
      canGoDirectlyToFavoriteTeamLeaderboard
    ) {
      navigate(
        generatePathWithSearch(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG, {
          slug: myFavoriteTeamOnboardingSo5Leaderboard.slug,
        })
      );
    } else if (mutationData) {
      setMutationPayload(mutationData.updateFavoriteTeam);
    }
  };

  const showEmptyState = !loading && !clubs.length;

  const onClosePromoDialog = () => {
    navigate(
      generatePathWithSearch(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG, {
        slug: onboardingPromotionalEvent?.so5Leaderboard.slug || '',
      })
    );
  };

  return (
    <Root>
      <Content>
        <HeadlineL>
          <FormattedMessage
            id="Onboarding.FavoriteClubSelection.title"
            defaultMessage="Select your favorite club"
          />
        </HeadlineL>
        <SearchInput
          placeholder={formatMessage(inputPlaceholder)}
          value={inputValue}
          onChange={onSearchChange}
        />
        {showEmptyState ? (
          <EmptyStateWrapper>
            <Shield width="32px" height="32px" />
            <BodyL>
              <FormattedMessage
                id="SelectFavoriteClub.emptyState"
                defaultMessage="No Club found"
              />
            </BodyL>
          </EmptyStateWrapper>
        ) : (
          <ClubGrid>
            {clubs.map(({ slug, name, pictureUrl }) => (
              <ButtonBase
                key={slug}
                type="button"
                onClick={async () => onSelectClub(slug)}
              >
                <Club>
                  {slug === selectedSlug && mutationLoading ? (
                    <LoadingIndicator small />
                  ) : (
                    <>
                      <ClubImg src={pictureUrl || placeholder} alt="" />
                      <LabelS bold>{name}</LabelS>
                    </>
                  )}
                </Club>
              </ButtonBase>
            ))}
          </ClubGrid>
        )}
        <InfiniteScrollLoader />
      </Content>
      {mutationPayload?.team && mutationPayload?.userSportProfile && (
        <PlayNowOrLater
          team={mutationPayload.team}
          userSportProfile={mutationPayload.userSportProfile}
        />
      )}
      {dialogPromotionalEvent && (
        <OnboardingPromoRecapDialog
          open
          onClose={onClosePromoDialog}
          onboardingPromotionalEvent={dialogPromotionalEvent}
        />
      )}
    </Root>
  );
};
